import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './GalleryLoadingOverlay.css';

function GalleryLoadingOverlay({ 
  isVisible, 
  galleryData, 
  assets = {}, 
  roomLoaded = false,
  texturesLoaded = false,
  avatarLoaded = false,
  onLoadingComplete
}) {
  const [loadingSteps, setLoadingSteps] = useState([
    { id: 'gallery', label: 'Loading gallery data', status: 'pending' },
    { id: 'room', label: 'Setting up room', status: 'pending' },
    { id: 'textures', label: 'Loading room textures', status: 'pending' },
    { id: 'assets', label: 'Loading gallery assets', status: 'pending' },
    { id: 'avatar', label: 'Loading character', status: 'pending' },
    { id: 'ready', label: 'Ready to explore', status: 'pending' }
  ]);

  const [assetsLoaded, setAssetsLoaded] = useState(0);
  const totalAssets = Object.keys(assets).length;

  // Update gallery data step
  useEffect(() => {
    if (galleryData && isVisible) {
      setLoadingSteps(prev => prev.map((step, index) => 
        index === 0 ? { ...step, status: 'completed' } : step
      ));
    }
  }, [galleryData, isVisible]);

  // Update room step
  useEffect(() => {
    if (roomLoaded && isVisible) {
      setLoadingSteps(prev => prev.map((step, index) => 
        index === 1 ? { ...step, status: 'completed' } : step
      ));
    }
  }, [roomLoaded, isVisible]);

  // Update textures step
  useEffect(() => {
    if (texturesLoaded && isVisible) {
      setLoadingSteps(prev => prev.map((step, index) => 
        index === 2 ? { ...step, status: 'completed' } : step
      ));
    }
  }, [texturesLoaded, isVisible]);

  // Update assets step (when most assets are loaded)
  useEffect(() => {
    if (totalAssets > 0 && assetsLoaded >= Math.ceil(totalAssets * 0.8)) {
      setLoadingSteps(prev => prev.map((step, index) => 
        index === 3 ? { ...step, status: 'completed' } : step
      ));
    }
  }, [assetsLoaded, totalAssets]);

  // Update avatar step
  useEffect(() => {
    if (avatarLoaded && isVisible) {
      setLoadingSteps(prev => prev.map((step, index) => 
        index === 4 ? { ...step, status: 'completed' } : step
      ));
    }
  }, [avatarLoaded, isVisible]);

  // Just hide after a reasonable time when basic stuff is loaded
  useEffect(() => {
    if (galleryData && roomLoaded && texturesLoaded && isVisible) {
      // Hide after 3 seconds when room is ready
      setTimeout(() => {
        onLoadingComplete?.();
      }, 3000);
    }
  }, [galleryData, roomLoaded, texturesLoaded, isVisible, onLoadingComplete]);

  if (!isVisible) return null;

  const completedSteps = loadingSteps.filter(step => step.status === 'completed').length;
  const progress = (completedSteps / loadingSteps.length) * 100;

  return createPortal(
    <div className="gallery-loading-overlay">
      <div className="loading-content">
        <div className="loading-header">
          <h2>Loading Gallery</h2>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="progress-text">{Math.round(progress)}%</span>
        </div>

        <div className="loading-steps">
          {loadingSteps.map((step, index) => {
            // Determine if this is the current step (first pending step)
            const isCurrentStep = step.status === 'pending' && 
                                 loadingSteps.slice(0, index).every(s => s.status === 'completed');
            
            return (
              <div 
                key={step.id} 
                className={`loading-step ${step.status} ${isCurrentStep ? 'current' : ''}`}
              >
                <div className="step-icon">
                  {step.status === 'completed' ? '✓' : 
                   isCurrentStep ? '○' : '○'}
                </div>
                <span className="step-label">{step.label}</span>
              </div>
            );
          })}
        </div>

        <div className="loading-tip">
          <p>You can look around while the gallery loads</p>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default GalleryLoadingOverlay;